import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast/headless';
import { useSocket } from '../hooks/useSocket';
import { useAuthStore } from '../hooks/useAuthStore';
import { useTranslation } from 'react-i18next';
import soundController from '../sound/SoundController';
import PlayerLeftModal from './PlayerLeftModal';

// Game component registry - dynamically import game components
const gameComponents = {
  'tic-tac-toe': lazy(() => import('../games/tic-tac-toe/TicTacToeGame.jsx')),
  'dots-and-boxes': lazy(() => import('../../../games/plugins/dots-and-boxes/client/DotsAndBoxesGame.jsx')),
  'red-sea': lazy(() => import('../../../games/plugins/red-sea/client/RedSea.jsx')),
  'old-testament': lazy(() => import('../../../games/plugins/old-testament/client/OldTestamentGame.jsx')),
  'jericho': lazy(() => import('../../../games/plugins/jericho/client/JerichoGameAdapter.jsx')),
  'materialism': lazy(() => import('../../../games/plugins/materialism/client/MaterialismGame.jsx')),
  'coming-attractions': lazy(() => import('../../../games/plugins/coming-attractions/client/ComingAttractionsGame.jsx')),
  'footsteps-in-the-wilderness': lazy(() => import('../../../games/plugins/footsteps-in-the-wilderness/client/FootstepsGame.jsx')),
  'knock-knock': lazy(() => import('../../../games/plugins/knock-knock/client/KnockKnockGame.jsx')),
  'bible-baseball': lazy(() => import('../../../games/plugins/bible-baseball/client/BibleBaseballGame.jsx')),
  'bible-match-n-tell': lazy(() => import('../../../games/plugins/bible-match-n-tell/client/BibleMatchNTellGame.jsx')),
  'great-commission': lazy(() => import('../../../games/plugins/great-commission/client/GreatCommissionGame.jsx'))
};

const DynamicGameLoader = ({ currentGame }) => {
  // A gameRejoined snapshot may arrive BEFORE this component mounts (page
  // reload mid-game) — useSocket stores it, and the effect below applies it
  // whenever it appears, regardless of event/mount timing.
  const rejoinSnapshot = useSocket(state => state.rejoinSnapshot);
  // Knock Knock: private hand of cards. Captured in useSocket itself (not
  // a local listener here) because the reconnect-time hand_update can
  // arrive before this component mounts — same race gameRejoined/
  // rejoinSnapshot already solves above.
  const myHand = useSocket(state => state.myHand);

  const [gameState, setGameState] = useState(null);
  const [gameError, setGameError] = useState(null);
  const [answerFeedback, setAnswerFeedback] = useState(null);
  const [personalQuestionData, setPersonalQuestionData] = useState(null);
  const [playerLeftModalData, setPlayerLeftModalData] = useState(null);
  // Jericho server-driven async loop: phase received from turn_phase events.
  const [phase, setPhase] = useState('idle');
  // Bible Match n Tell: transient flash data for the two just-resolved
  // cards, since gameState.board already shows them matched/hidden by the
  // time the event lands — the component uses this to render the brief
  // "here's what those two cards were" beat itself.
  const [bmtMatchFlash, setBmtMatchFlash] = useState(null);
  const [bmtMismatchFlash, setBmtMismatchFlash] = useState(null);
  // For non-active players: fires jericho:applyWallCapture after a delay that
  // mirrors the active player's sound chain + wallCaptureTimer offset.
  const spectatorWallTimerRef = useRef(null);
  const { socket, leaveGame } = useSocket();
  const { user } = useAuthStore();
  const { t, i18n } = useTranslation('game');
  const navigate = useNavigate();

  // Handle game state updates from server
  useEffect(() => {
    if (!socket) return;

    const handlers = {
      gameStarted: (game) => {
        console.log('[DynamicGameLoader] gameStarted event (lobby) received:', game);
        // This is the old game object from lobby, not the actual game instance
      },
      game_started: (data) => {
        console.log('[DynamicGameLoader] game_started event received:', data);
        setGameState(data.gameState);
        setGameError(null);
      },
      move_made: (data) => {
        setGameState(data.gameState);
      },
      player_moved: (data) => {
        // Materialism, Coming Attractions, and Footsteps in the
        // Wilderness sequence their own per-hop clicks after the dice/
        // spin animation; the generic sound here would fire mid-tumble,
        // before the token actually starts moving.
        const ownMoveSound = ['materialism', 'coming-attractions', 'footsteps-in-the-wilderness'];
        if (!ownMoveSound.includes(currentGame?.type)) soundController.play('player_moved');
        setGameState(data.gameState);
      },
      wave_moved: (data) => {
        soundController.play('wave_moved');
        setGameState(data.gameState);
      },
      game_ended: (data) => {
        // Materialism plays the win/lose sting itself when its win modal
        // opens — this event arrives before the roll/hop animation ends.
        if (currentGame?.type !== 'materialism') {
          const winner = data.gameState?.winner;
          if (winner === user?.id) {
            soundController.play('game_won');
          } else if (winner && winner !== user?.id) {
            soundController.play('game_lost');
          }
        }
        setGameState(data.gameState);
      },
      game_reset: (data) => {
        setGameState(data.gameState);
      },
      action_error: (data) => {
        // Red Sea only: render as the app's fade-in/pause/slide-out-right
        // toast instead of the static banner other games still use below.
        if (currentGame?.type === 'red-sea') {
          toast.error(data.message, { duration: 1000 });
          return;
        }
        setGameError(data.message);
        setTimeout(() => setGameError(null), 3000);
      },
      // NEW v2.0: Handle phase validation rejections
      action_rejected: (data) => {
        console.warn('[Phase Validation] Action rejected:', data.message);
        const message = data.message || 'Action not allowed at this time';
        if (currentGame?.type === 'red-sea') {
          toast.error(message, { duration: 1000 });
          return;
        }
        setGameError(message);
        setTimeout(() => setGameError(null), 4000);
      },
      // Red Sea specific events
      dice_rolled: (data) => {
        // Jericho manages dice sound timing internally via useTurnController.
        // Materialism, Coming Attractions, and Footsteps in the Wilderness
        // play their own dice_roll→roll_result chain synced to their roll/
        // spin animation.
        // TODO: refactor — unify dice roll sound logic across games so both
        // Red Sea and Jericho trigger from this socket event layer, removing
        // the need for this game-type guard.
        const ownDiceSound = ['jericho', 'materialism', 'coming-attractions', 'footsteps-in-the-wilderness'];
        if (!ownDiceSound.includes(currentGame?.type)) soundController.play('dice_roll');
        setGameState(data.gameState);
      },
      tile_selected: (data) => {
        setGameState(data.gameState);
      },
      // Red Sea: a player reached the finish tile but others are still
      // racing — the match doesn't end here, RedSea.jsx owns the
      // interim-placement announcement and its sound.
      player_finished: (data) => {
        setGameState(data.gameState);
      },
      // Red Sea: someone proposed/agreed to a post-win "new game" vote —
      // RedSea.jsx reads gameState.newGameVote to render the tally.
      new_game_vote_updated: (data) => {
        setGameState(data.gameState);
      },
      // Red Sea: the match is paused for a placement announcement (or that
      // pause just resolved) — RedSea.jsx reads gameState.pendingFinishAck.
      finish_ack_updated: (data) => {
        setGameState(data.gameState);
      },
      finish_acknowledged: (data) => {
        setGameState(data.gameState);
      },
      starting_tile_selected: (data) => {
        soundController.play('start_position');
        setGameState(data.gameState);
      },
      all_starting_tiles_selected: (data) => {
        setGameState(data.gameState);
      },
      game_phase_changed: (data) => {
        setGameState(data.gameState);
      },
      tile_selection_complete: (data) => {
        setGameState(data.gameState);
      },
      turn_ended: (data) => {
        setGameState(data.gameState);
      },
      player_disconnected: (data) => {
        setGameState(data.gameState);
      },
      question_triggered: (data) => {
        setGameState(data.gameState);
      },
      question_triggered_personal: (data) => {
        setPersonalQuestionData(data);
        setGameState(data.gameState);
      },
      question_answered_correct: (data) => {
        soundController.play('answer_correct');
        // Bible Baseball: a correct answer here isn't just trivia — it's
        // the batter's trivia-gated swing landing as an actual base hit
        // (see game.js applyHit), so the crowd gets a cheer on top of the
        // generic correct-answer ding.
        if (currentGame?.type === 'bible-baseball' && data.effects?.some(e => e.type === 'hit')) {
          soundController.play('crowd_cheer');
        }
        setGameState(data.gameState);
        setAnswerFeedback({ isCorrect: true, data });
      },
      question_answered_incorrect: (data) => {
        soundController.play('answer_incorrect');
        setGameState(data.gameState);
        setAnswerFeedback({ isCorrect: false, data });
      },
      question_skipped: (data) => {
        setGameState(data.gameState);
      },
      // Footsteps in the Wilderness: the fork's path-choice hop, and the
      // temptation defense result (played like an answer_correct/
      // incorrect sting since it's the same "did it work" beat).
      path_chosen: (data) => {
        setGameState(data.gameState);
      },
      path_choice_triggered: (data) => {
        setGameState(data.gameState);
      },
      temptation_triggered: (data) => {
        setGameState(data.gameState);
      },
      defense_succeeded: (data) => {
        soundController.play('answer_correct');
        setGameState(data.gameState);
      },
      defense_failed: (data) => {
        soundController.play('answer_incorrect');
        setGameState(data.gameState);
      },
      game_continued: (data) => {
        setGameState(data.gameState);
      },
      player_left_game_modal: (data) => {
        setPlayerLeftModalData(data);
        // DON'T update gameState yet - wait for user acknowledgment
      },
      // Red Sea: a player left during setup (before the game started
      // racing) — lighter-weight than player_left_game_modal, just a toast.
      player_left_game: (data) => {
        toast(data.message || 'A player left the game');
        setGameState(data.gameState);
      },
      // Server-driven async-loop games (Jericho, Old Testament): all turn
      // phases arrive via this event. Old Testament feedback sounds are keyed
      // off gameState.lastResult inside OldTestamentGame.jsx.
      // React 18 batches setPhase + setGameState into a single re-render.
      turn_phase: (data) => {
        setPhase(data.phase);
        setGameState(data.gameState);
        // Wall capture: dispatch animation events so WallSections can animate
        // the gate/fall and DynamicGameLoader can play the horn sound.
        if ((data.phase === 'wall_captured' || data.phase === 'game_ended') && data.sectionId) {
          window.dispatchEvent(new CustomEvent('jericho:wallCapturedSection', {
            detail: { sectionId: data.sectionId }
          }));
          if (spectatorWallTimerRef.current) clearTimeout(spectatorWallTimerRef.current);
          spectatorWallTimerRef.current = setTimeout(() => {
            spectatorWallTimerRef.current = null;
            window.dispatchEvent(new CustomEvent('jericho:applyWallCapture'));
          }, 500);
        }
      },
      // Legacy Jericho events (kept for backward compatibility during transition)
      answer_evaluated: (data) => {
        setGameState(data.gameState);
      },
      wall_captured: (data) => {
        window.dispatchEvent(new CustomEvent('jericho:wallCapturedSection', {
          detail: { sectionId: data.capturedSectionId }
        }));
        setGameState(data.gameState);
        if (user?.id !== data.gameState.currentPlayer) {
          if (spectatorWallTimerRef.current) clearTimeout(spectatorWallTimerRef.current);
          spectatorWallTimerRef.current = setTimeout(() => {
            spectatorWallTimerRef.current = null;
            window.dispatchEvent(new CustomEvent('jericho:applyWallCapture'));
          }, 3000);
        }
      },
      trumpet_attempt: (data) => {
        setGameState(data.gameState);
      },
      trumpet_failed: (data) => {
        setGameState(data.gameState);
      },
      // Knock Knock: hand_dealt/hand_update are handled in useSocket.js
      // (see myHand above) — no local listener needed here.
      card_drawn: (data) => {
        setGameState(data.gameState);
      },
      pair_laid: (data) => {
        setGameState(data.gameState);
      },
      // Bible Baseball: team_selected fires during the pre-game roster
      // screen; pitch_thrown/at_bat_resolved carry the server's physics
      // trajectory that BibleBaseballGame.jsx replays via PhysicsScene.jsx.
      team_selected: (data) => {
        setGameState(data.gameState);
      },
      pitch_thrown: (data) => {
        setGameState(data.gameState);
      },
      at_bat_resolved: (data) => {
        setGameState(data.gameState);
      },
      // Bible Match n Tell
      card_flipped: (data) => {
        soundController.play('card_flip');
        setGameState(data.gameState);
      },
      match_found: (data) => {
        soundController.play('card_match');
        setBmtMatchFlash({
          positions: data.matchedPositions,
          imageId: data.imageId,
          playerId: data.playerId,
          key: Date.now()
        });
        setGameState(data.gameState);
      },
      card_mismatch: (data) => {
        soundController.play('card_mismatch');
        setBmtMismatchFlash({ cards: data.mismatchedCards, key: Date.now() });
        setGameState(data.gameState);
      },
      tell_advanced: (data) => {
        setGameState(data.gameState);
      },
      tell_finished: (data) => {
        setGameState(data.gameState);
      }
    };

    // Register event handlers
    Object.entries(handlers).forEach(([event, handler]) => {
      socket.on(event, handler);
    });

    return () => {
      // Cleanup event handlers — pass the handler so only OUR listener is
      // removed (a bare socket.off(event) would also strip listeners other
      // components registered for the same event, e.g. useSocket's)
      Object.entries(handlers).forEach(([event, handler]) => {
        socket.off(event, handler);
      });
      if (spectatorWallTimerRef.current) clearTimeout(spectatorWallTimerRef.current);
    };
  }, [socket]);

  // Apply the mid-game reconnect snapshot whenever it lands — covers both
  // orders: snapshot before this component mounted, or after.
  useEffect(() => {
    if (rejoinSnapshot && rejoinSnapshot.gameId === currentGame?.id) {
      console.log('[DynamicGameLoader] applying rejoin snapshot, phase:', rejoinSnapshot.currentPhase);
      if (rejoinSnapshot.gameState) setGameState(rejoinSnapshot.gameState);
      if (rejoinSnapshot.currentPhase) setPhase(rejoinSnapshot.currentPhase);
      setGameError(null);
    }
  }, [rejoinSnapshot, currentGame?.id]);

  // Play the chofar horn (4.99s) when the wall-capture animation is triggered.
  // The server's TIMING.wallAnimation (5500ms) budgets for this full duration.
  useEffect(() => {
    const handler = () => soundController.play('chofar_horn');
    window.addEventListener('jericho:applyWallCapture', handler);
    return () => window.removeEventListener('jericho:applyWallCapture', handler);
  }, []);

  // Handle game actions from the game component
  const handleGameAction = (action) => {
    if (action.type === 'return_to_lobby') {
      leaveGame();
      navigate('/lobby');
      return;
    }
    if (!socket || !currentGame) return;
    socket.emit('gameAction', {
      gameId: currentGame.id,
      action
    });
  };

  // Handle starting game instance
  const handleStartGame = () => {
    if (!socket || !currentGame) return;

    socket.emit('startGameInstance', {
      gameId: currentGame.id
    });
  };

  // Handle player left modal actions
  const handleContinueGame = () => {
    if (playerLeftModalData?.gameState) {
      setGameState(playerLeftModalData.gameState); // Apply state now
      setPlayerLeftModalData(null); // Close modal
      soundController.play('player_moved'); // Optional sound

      // Emit acknowledgment to server so bots can continue
      if (socket && currentGame) {
        socket.emit('acknowledgePlayerLeft', {
          gameId: currentGame.id
        });
      }
    }
  };

  const handleQuitGame = () => {
    setPlayerLeftModalData(null);
    leaveGame();
    navigate('/lobby');
  };

  // Render appropriate game state
  const renderGameContent = () => {
    // If game hasn't been started yet
    if (currentGame.status === 'waiting' && !gameState) {
      return (
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">
            {t('loader.waitingToStart')}
          </h3>
          <p className="text-gray-400 mb-4">
            {currentGame.host === user?.id
              ? t('loader.hostMessage')
              : t('loader.playerMessage')
            }
          </p>
          {currentGame.host === user?.id && (
            <button
              onClick={handleStartGame}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
            >
              {t('loader.startGame')}
            </button>
          )}
        </div>
      );
    }

    // If we have a game state, render the specific game component
    if (gameState) {
      const GameComponent = gameComponents[currentGame.type];
      
      if (!GameComponent) {
        return (
          <div className="text-center">
            <h3 className="text-lg font-semibold text-red-400 mb-2">
              {t('loader.gameNotSupported')}
            </h3>
            <p className="text-gray-400">
              {t('loader.gameTypeNotAvailable', { type: currentGame.type })}
            </p>
          </div>
        );
      }

      return (
        <Suspense fallback={
          <div className="text-center">
            <div className="text-white text-lg">{t('loader.loadingGame')}</div>
          </div>
        }>
          <GameComponent
            gameState={gameState}
            onAction={handleGameAction}
            currentUserId={user?.id}
            answerFeedback={answerFeedback}
            clearAnswerFeedback={() => setAnswerFeedback(null)}
            soundController={soundController}
            language={i18n.language}
            personalQuestionData={personalQuestionData}
            clearPersonalQuestion={() => setPersonalQuestionData(null)}
            myHand={myHand}
            phase={phase}
            leaveGame={leaveGame}
            bmtMatchFlash={bmtMatchFlash}
            bmtMismatchFlash={bmtMismatchFlash}
          />
        </Suspense>
      );
    }

    // Default fallback for other states
    return (
      <div className="text-center">
        <h3 className="text-lg font-semibold text-white mb-2">
          {t('loader.gameStatus', { status: currentGame.status })}
        </h3>
        <p className="text-gray-400">
          {(currentGame.status === 'playing' || currentGame.status === 'in-progress')
            ? t('loader.gameRunningNoState')
            : t('loader.gameSessionEnded')
          }
        </p>
      </div>
    );
  };

  return (
    <>
      <div className="w-full h-full p-0 relative">
        {gameError && (
          <div className="absolute top-4 left-4 right-4 z-50 p-3 bg-red-900/50 border border-red-500 rounded-lg">
            {/* This banner spans edge-to-edge on every screen size, so a
                short message left-aligned inside it reads as stranded on
                mobile's narrower width — centered there, back to
                left-aligned once there's enough width (md+) that
                centering would just look like an odd gap next to the
                border. */}
            <p className="text-red-200 text-sm text-center md:text-left">{gameError}</p>
          </div>
        )}

        <div className="w-full h-full flex items-center justify-center">
          {renderGameContent()}
        </div>
      </div>

      {/* Player Left Modal */}
      <PlayerLeftModal
        isVisible={!!playerLeftModalData}
        leavingPlayerName={playerLeftModalData?.leavingPlayerName}
        newCurrentPlayerName={playerLeftModalData?.newCurrentPlayerName}
        remainingPlayerCount={playerLeftModalData?.remainingPlayerCount}
        onContinue={handleContinueGame}
        onQuit={handleQuitGame}
      />
    </>
  );
};

export default DynamicGameLoader;